import { borderRadius } from '../KestrixDesignSystem.jsx'
import { colorTokens } from '../tokens/colors.ts'

/**
 * The Figma compass (node 852:46289) is drawn on a 336.667px halo. Every
 * length here is a Figma length scaled to the rendered halo, so the geometry
 * matches the design at any size. Text is the exception: scaled straight down
 * it would be unreadable, so each variant sets legible sizes instead.
 */
const FIGMA_HALO_SIZE = 336.667

/** Figma radii from the dial centre. */
const FIGMA_LABEL_RADIUS = 147
const FIGMA_DEGREE_MARK_RADIUS = 196
const FIGMA_TOP_POINTER_OFFSET = -226.36
/** Top edge of the top pointer, the furthest thing from the centre. */
const FIGMA_FRAME_RADIUS = 238

export type DialSize = {
  /** Rendered halo diameter in px. */
  readonly haloSize: number
  /** Rendered frame side in px. The halo sits at its centre. */
  readonly frameSize: number
}

export const COLLAPSED_DIAL: DialSize = { haloSize: 56, frameSize: 56 }

const EXPANDED_HALO_SIZE = 200
export const EXPANDED_DIAL: DialSize = {
  haloSize: EXPANDED_HALO_SIZE,
  frameSize: (2 * FIGMA_FRAME_RADIUS * EXPANDED_HALO_SIZE) / FIGMA_HALO_SIZE
}

/** Builds the absolute positions of every dial part for one dial size. */
export const createDialSx = ({ haloSize, frameSize }: DialSize) => {
  const scaled = (figmaLength: number): number =>
    (figmaLength * haloSize) / FIGMA_HALO_SIZE
  const centre = frameSize / 2

  /** A box of a Figma size whose centre sits at a Figma offset from the dial centre. */
  const centredAt = (
    figmaWidth: number,
    figmaHeight: number,
    figmaOffsetX = 0,
    figmaOffsetY = 0
  ) =>
    ({
      position: 'absolute',
      width: scaled(figmaWidth),
      height: scaled(figmaHeight),
      left: centre + scaled(figmaOffsetX) - scaled(figmaWidth) / 2,
      top: centre + scaled(figmaOffsetY) - scaled(figmaHeight) / 2
    }) as const

  /**
   * Puts an item on a circle at its bearing, then turns it back by the map
   * bearing so its text stays upright while the dial turns.
   */
  const onCircle =
    (figmaRadius: number) => (itemBearing: number, mapBearing: number) => {
      const radians = (itemBearing * Math.PI) / 180
      return {
        position: 'absolute',
        left: centre + scaled(figmaRadius) * Math.sin(radians),
        top: centre - scaled(figmaRadius) * Math.cos(radians),
        transform: `translate(-50%, -50%) rotate(${mapBearing}deg)`
      } as const
    }

  /** A tick runs from the dial centre to the gauge ring's outer edge. */
  const tickLength = scaled(121.2)

  return {
    frame: { position: 'relative', width: frameSize, height: frameSize },
    halo: centredAt(FIGMA_HALO_SIZE, FIGMA_HALO_SIZE),
    gaugeRing: centredAt(242.4, 242.4),
    innerDisc: centredAt(193.92, 193.92),
    northPointer: centredAt(13.1744, 13.2952, 0, -129.28),
    highlight: {
      position: 'absolute',
      width: scaled(109.615),
      height: scaled(57.3611),
      left: centre + scaled(-55.54),
      top: centre + scaled(-107.73)
    },
    topPointer: {
      ...centredAt(23.1957, 23.389, 0, FIGMA_TOP_POINTER_OFFSET),
      transform: 'scaleY(-1)'
    },
    tick: {
      position: 'absolute',
      width: '1px',
      height: tickLength,
      left: centre - 0.5,
      top: centre - tickLength,
      transformOrigin: 'bottom center',
      backgroundColor: colorTokens.global.primary[200]
    },
    placeLabel: onCircle(FIGMA_LABEL_RADIUS),
    placeDegreeMark: onCircle(FIGMA_DEGREE_MARK_RADIUS)
  } as const
}

export const TICK_STEP_DEGREES = 15

/** Turns with the map. Mapbox fires a rotate event every frame, so no CSS transition is needed. */
export const LAYER_SX = { position: 'absolute', inset: 0 } as const

/** Stays still over the turning layer and lets clicks through to the labels. */
export const FIXED_LAYER_SX = { ...LAYER_SX, pointerEvents: 'none' } as const

const BUTTON_STATES = colorTokens.buttonStates.secondary

const LABEL_BUTTON_SX = {
  p: '1px 3px',
  border: 'none',
  borderRadius: borderRadius.base,
  backgroundColor: 'transparent',
  fontFamily: 'inherit',
  lineHeight: 1,
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: BUTTON_STATES.hover.surface,
    color: colorTokens.semantic.brand.primary
  },
  '&:focus-visible': {
    outline: `2px solid ${colorTokens.semantic.brand.primary}`,
    outlineOffset: 1
  },
  '&:disabled': {
    cursor: 'default',
    backgroundColor: 'transparent'
  }
} as const

export const CARDINAL_LABEL_SX = {
  ...LABEL_BUTTON_SX,
  fontSize: 12,
  fontWeight: 700,
  color: colorTokens.text.subtextNeutral
} as const

export const INTERCARDINAL_LABEL_SX = {
  ...LABEL_BUTTON_SX,
  fontSize: 10,
  fontWeight: 500,
  color: colorTokens.global.neutral[600]
} as const

export const DEGREE_MARK_SX = {
  fontSize: 10,
  fontWeight: 500,
  lineHeight: 1,
  color: colorTokens.semantic.brand.primary
} as const

export const COLLAPSED_NORTH_SX = {
  fontSize: 9,
  fontWeight: 700,
  lineHeight: 1,
  color: colorTokens.text.subtextNeutral
} as const

const DEGREES_BASE_SX = {
  ...LAYER_SX,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 600,
  lineHeight: 1,
  color: colorTokens.semantic.brand.base.white
} as const

export const EXPANDED_DEGREES_SX = { ...DEGREES_BASE_SX, fontSize: 26 } as const
export const COLLAPSED_DEGREES_SX = { ...DEGREES_BASE_SX, fontSize: 11 } as const
